import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import SeasonReview from '../components/SeasonReview'

async function sendInviteEmail({ toEmail, clubName, squadName, role, invitedByName, coachName }) {
  const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/send-invite-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.REACT_APP_SUPABASE_ANON_KEY },
    body: JSON.stringify({ toEmail, clubName, squadName, role, invitedByName, coachName })
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Failed to send email')
  return data
}

export default function Admin({ session, profile }) {
  const [coaches, setCoaches] = useState([])
  const [squads, setSquads] = useState([])
  const [pendingCoaches, setPendingCoaches] = useState([])
  const [liveMatches, setLiveMatches] = useState([])
const [activeTab, setActiveTab] = useState('pending')
  const [editingSquad, setEditingSquad] = useState(null)
  const [squadEditForm, setSquadEditForm] = useState({ name: '', current_grade: '', sport: '', gender: '' })
  const [squadSaving, setSquadSaving] = useState(false)
  const [showNewSeason, setShowNewSeason] = useState(null)
  const [newSeasonYear, setNewSeasonYear] = useState(String(new Date().getFullYear() + 1))
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteCoachName, setInviteCoachName] = useState('')
  const [inviteSquadId, setInviteSquadId] = useState('')
  const [inviteRole, setInviteRole] = useState('coach')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => { if (profile) { loadCoaches(); loadSquads(); loadLiveMatches() } }, [profile])

  async function loadSquads() {
    const isClubAdmin = profile?.role === 'club_admin'
    let query = supabase.from('squads').select('*').eq('club_id', profile.club_id).order('name')
    if (!isClubAdmin && profile?.squadIds && profile.squadIds.length > 0) {
      query = query.in('id', profile.squadIds)
    }
    const { data } = await query
    setSquads(data || [])
  }

  async function loadCoaches() {
    const isClubAdmin = profile?.role === 'club_admin'
    const { data } = await supabase.from('profiles')
      .select('*, squad_members!squad_members_profile_id_fkey(squad_id, role, approved, squads(name))')
      .eq('club_id', profile.club_id)
      .neq('id', session.user.id)
      .order('full_name')
    if (data) {
      if (isClubAdmin) {
        setPendingCoaches(data.filter(c => !c.approved))
        setCoaches(data.filter(c => c.approved))
      } else {
        const mySquadIds = profile?.squadIds || []
        const pending = data.filter(c => {
          if (c.approved) return false
          const members = c.squad_members || []
          return members.some(sm => mySquadIds.includes(sm.squad_id))
        })
        const active = data.filter(c => {
          if (!c.approved) return false
          const members = c.squad_members || []
          return members.some(sm => mySquadIds.includes(sm.squad_id))
        })
        setPendingCoaches(pending)
        setCoaches(active)
      }
    }
  }

  async function loadLiveMatches() {
    const isClubAdmin = profile?.role === 'club_admin'
    let query = supabase.from('fixtures')
      .select('id, opponent, tracked_by, tracked_by_name, squad_id, squads(name)')
      .eq('club_id', profile.club_id)
      .eq('status', 'live')
      .not('tracked_by', 'is', null)
    if (!isClubAdmin && profile?.squadIds && profile.squadIds.length > 0) {
      query = query.in('squad_id', profile.squadIds)
    }
    const { data } = await query
    setLiveMatches(data || [])
  }

  async function takeOverTracking(fixtureId) {
    if (!window.confirm('Take over tracking of this match? The current tracker will lose control.')) return
    await supabase.from('fixtures').update({
      tracked_by: profile.id,
      tracked_by_name: profile.full_name
    }).eq('id', fixtureId)
    setMessage('You have taken over match tracking.')
    loadLiveMatches()
  }

  async function sendInvite() {
    if (!inviteEmail.trim()) { setError('Please enter an email address.'); return }
    if (!inviteCoachName.trim()) { setError('Please enter the coach\'s name.'); return }
    if (!inviteSquadId) { setError('Please select a squad to assign them to.'); return }
    setLoading(true); setError(''); setMessage('')
    try {
      const { error: inviteStoreError } = await supabase.from('pending_invites').insert({
        email: inviteEmail.trim().toLowerCase(),
        club_id: profile.club_id,
        squad_id: inviteSquadId,
        role: inviteRole,
        invited_by: session.user.id
      })
      if (inviteStoreError) { setError('Error storing invite: ' + inviteStoreError.message); setLoading(false); return }
      const squad = squads.find(s => s.id === inviteSquadId)
      const clubName = profile.clubs?.club_name || profile.clubs?.name || 'your club'
      await sendInviteEmail({
        toEmail: inviteEmail.trim().toLowerCase(),
        clubName, squadName: squad?.name, role: inviteRole,
        invitedByName: profile.full_name || 'Your admin',
        coachName: inviteCoachName.trim()
      })
      setMessage('✅ Invite sent to ' + inviteEmail + '!')
    setInviteEmail(''); setInviteCoachName(''); setInviteSquadId(''); setInviteRole('coach')
      setShowInvite(false); loadCoaches()
    } catch (e) {
      setMessage('Invite stored but email failed. Share this link manually: https://gaamatchtracker.ie?invite=' + encodeURIComponent(inviteEmail.trim().toLowerCase()))
    }
    setLoading(false)
  }

  async function approveCoach(coachId, squadId, role) {
    if (!squadId) { alert('Please select a squad to assign this coach to.'); return }
    setLoading(true)
    await supabase.from('profiles').update({ approved: true, role: role || 'coach' }).eq('id', coachId)
    const { data: existing } = await supabase.from('squad_members')
      .select('id').eq('profile_id', coachId).eq('squad_id', squadId).maybeSingle()
    if (existing) {
      await supabase.from('squad_members').update({ approved: true, role: role || 'coach' }).eq('profile_id', coachId).eq('squad_id', squadId)
    } else {
      await supabase.from('squad_members').insert({ squad_id: squadId, profile_id: coachId, role: role || 'coach', approved: true, invited_by: session.user.id })
    }
    setMessage('Coach approved and assigned to squad.')
    loadCoaches(); setLoading(false)
  }

  async function rejectCoach(coachId) {
    if (!window.confirm('Reject and remove this coach request?')) return
    await supabase.from('profiles').delete().eq('id', coachId)
    loadCoaches()
  }

  async function assignToSquad(coachId, squadId, role) {
    if (!squadId) return
    await supabase.from('squad_members').upsert({ squad_id: squadId, profile_id: coachId, role: role || 'coach', approved: true, invited_by: session.user.id })
    setMessage('Coach assigned to squad.')
    loadCoaches()
  }

  async function removeFromSquad(coachId, squadId) {
    if (!window.confirm('Remove this coach from the squad?')) return
    await supabase.from('squad_members').delete().eq('profile_id', coachId).eq('squad_id', squadId)
    loadCoaches()
  }

  async function updateRole(coachId, newRole) {
    await supabase.from('profiles').update({ role: newRole }).eq('id', coachId)
    setMessage('Role updated.')
    loadCoaches()
  }

async function removeCoach(coachId) {
    if (!window.confirm('Remove this coach from all squads in the club? Their account will stay active, but they\'ll lose access until reassigned to a squad.')) return
    await supabase.from('squad_members').delete().eq('profile_id', coachId)
    loadCoaches()
  }

  const roleLabel = (role) => {
    if (role === 'club_admin') return { label: 'Club Admin', color: '#185FA5', bg: '#E6F1FB' }
    if (role === 'squad_admin') return { label: 'Squad Admin', color: '#854F0B', bg: '#FAEEDA' }
    return { label: 'Coach', color: '#3B6D11', bg: '#EAF3DE' }
  }

    async function saveSquadEdit() {
    if (!editingSquad || !squadEditForm.name.trim()) return
    setSquadSaving(true)
    await supabase.from('squads').update({
      name: squadEditForm.name.trim(),
      current_grade: squadEditForm.current_grade || null,
      sport: squadEditForm.sport,
      gender: squadEditForm.gender
    }).eq('id', editingSquad.id)
    setEditingSquad(null)
    setSquadSaving(false)
    loadCoaches(); loadSquads()
  }

  async function startNewSeason(squad) {
    const year = parseInt(newSeasonYear)
    if (!year || year < 2020 || year > 2050) { alert('Please enter a valid year.'); return }
    if (!window.confirm('Start ' + year + ' season for ' + squad.name + '? Future fixtures will default to ' + year + '. Existing fixtures stay in their original season.')) return
    await supabase.from('squads').update({
      current_grade: squadEditForm.current_grade || squad.current_grade
    }).eq('id', squad.id)
    setShowNewSeason(null)
    alert('New ' + year + ' season started for ' + squad.name + '. Add fixtures as normal — they\'ll default to ' + year + '.')
    loadCoaches(); loadSquads()
  }

  const isClubAdmin = profile?.role === 'club_admin'
  const clubName = profile?.clubs?.club_name || profile?.clubs?.name || 'Your Club'
  return (
    <div>
      <div style={header}>
        <h2 style={h2}>Admin</h2>
        <button style={addBtn} onClick={() => { setShowInvite(!showInvite); setError(''); setMessage('') }}>
          {showInvite ? 'Cancel' : '+ Invite Coach'}
        </button>
      </div>

    {/* Club info */}
      <div style={{ padding: '10px 16px', background: '#f8f8f8', borderBottom: '0.5px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '16px', flexShrink: 0 }}>🏐</div>
        <div>
          <p style={{ fontSize: '13px', fontWeight: '500' }}>{clubName}</p>
          <p style={{ fontSize: '11px', color: '#888' }}>{isClubAdmin ? 'Club Admin' : 'Squad Admin'} · {coaches.length + 1} active · {pendingCoaches.length} pending</p>
        </div>
      </div>

      <ClubOfficers profile={profile} isClubAdmin={isClubAdmin} isSquadAdmin={profile?.role === 'squad_admin'} />

      {/* Live matches */}
      {liveMatches.length > 0 && (
        <div style={{ margin: '12px 16px', padding: '14px', background: '#fff5f5', border: '0.5px solid #f5c6c6', borderRadius: '12px' }}>
          <p style={{ fontSize: '13px', fontWeight: '500', marginBottom: '10px', color: '#A32D2D' }}>🔴 Live Matches</p>
          {liveMatches.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid #f5c6c6' }}>
              <div>
                <p style={{ fontSize: '13px', fontWeight: '500' }}>{m.squads?.name} vs {m.opponent}</p>
                <p style={{ fontSize: '11px', color: '#888' }}>Tracked by {m.tracked_by_name}</p>
              </div>
              {m.tracked_by !== profile?.id && (
                <button onClick={() => takeOverTracking(m.id)}
                  style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '8px', border: '0.5px solid #A32D2D', background: '#FCEBEB', color: '#A32D2D', cursor: 'pointer' }}>
                  Take Over
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {message && (
        <div style={{ background: '#EAF3DE', padding: '10px 16px', fontSize: '13px', color: '#3B6D11', borderBottom: '0.5px solid #c5e3a6', lineHeight: '1.5' }}>
          {message}
          <button onClick={() => setMessage('')} style={{ float: 'right', background: 'none', border: 'none', color: '#3B6D11', cursor: 'pointer', fontSize: '16px' }}>✕</button>
        </div>
      )}

      {error && (
        <div style={{ background: '#FCEBEB', padding: '10px 16px', fontSize: '13px', color: '#A32D2D', borderBottom: '0.5px solid #f5c6c6' }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: '16px' }}>✕</button>
        </div>
      )}

      {showInvite && (
        <div style={formBox}>
          <p style={formTitle}>Invite a coach</p>
          <p style={{ fontSize: '12px', color: '#888', marginBottom: '12px', lineHeight: '1.5' }}>
            Enter their email and select which squad and role to assign them.
          </p>
          <input style={inputStyle} type="text" placeholder="Coach full name *"
            value={inviteCoachName} onChange={e => setInviteCoachName(e.target.value)} />
          <input style={inputStyle} type="email" placeholder="Coach email address *"
            value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
          <select style={inputStyle} value={inviteSquadId} onChange={e => setInviteSquadId(e.target.value)}>
            <option value="">Assign to squad *</option>
            {squads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select style={inputStyle} value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
            <option value="coach">Coach</option>
            <option value="squad_admin">Squad Admin</option>
            {isClubAdmin && <option value="club_admin">Club Admin</option>}
          </select>
          <button style={{ ...primaryBtn, opacity: loading ? 0.7 : 1 }} onClick={sendInvite} disabled={loading}>
            {loading ? 'Sending invite...' : '📧 Send Invite'}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '0.5px solid #e0e0e0' }}>
        {[
        { id: 'pending', label: 'Pending (' + pendingCoaches.length + ')' },
          { id: 'coaches', label: 'Coaches (' + coaches.length + ')' },
          { id: 'squads', label: 'Squads (' + squads.length + ')' },
          { id: 'review', label: '📊 Review' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ flex: 1, padding: '12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: activeTab === t.id ? '#185FA5' : '#888', borderBottom: activeTab === t.id ? '2px solid #185FA5' : '2px solid transparent' }}>
            {t.label}
          </button>
        ))}
      </div>

    {activeTab === 'squads' && (
        <div style={{ padding: '12px 16px' }}>
          <p style={{ fontSize: '12px', color: '#888', marginBottom: '12px', lineHeight: '1.5' }}>
            Edit squad names, age groups, and start new seasons. The squad's public URL never changes.
          </p>
          {squads.map(s => (
            <div key={s.id} style={{ background: 'white', borderRadius: '12px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              {editingSquad?.id === s.id ? (
                <>
                  <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>Editing {s.name}</p>
                  <input style={adminInput} placeholder="Squad name *" value={squadEditForm.name}
                    onChange={e => setSquadEditForm({ ...squadEditForm, name: e.target.value })} />
                  <select style={adminInput} value={squadEditForm.current_grade}
                    onChange={e => setSquadEditForm({ ...squadEditForm, current_grade: e.target.value })}>
                    <option value="">No age grade set</option>
                    {['U12','U13','U14','U15','U16','U17','U18','Minor','U20','Senior'].map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  <select style={adminInput} value={squadEditForm.sport}
                    onChange={e => setSquadEditForm({ ...squadEditForm, sport: e.target.value })}>
                    {squadEditForm.gender === 'girls' ? (
                      <>
                        <option value="football">Gaelic Football</option>
                        <option value="camogie">Camogie</option>
                        <option value="dual_ladies">Dual (Football & Camogie)</option>
                      </>
                    ) : (
                      <>
                        <option value="football">Gaelic Football</option>
                        <option value="hurling">Hurling</option>
                        <option value="dual">Dual (Football & Hurling)</option>
                      </>
                    )}
                  </select>
                  <select style={adminInput} value={squadEditForm.gender}
                    onChange={e => setSquadEditForm({ ...squadEditForm, gender: e.target.value })}>
                    <option value="boys">Boys</option>
                    <option value="girls">Ladies</option>
                  </select>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    <button onClick={saveSquadEdit} disabled={squadSaving}
                      style={{ flex: 1, padding: '10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', opacity: squadSaving ? 0.7 : 1 }}>
                      {squadSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button onClick={() => setEditingSquad(null)}
                      style={{ flex: 1, padding: '10px', border: '0.5px solid #ccc', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '13px' }}>
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '14px', fontWeight: '500' }}>{s.name}</p>
                      <p style={{ fontSize: '11px', color: '#888' }}>
                        {s.sport === 'football' ? 'Gaelic Football' : s.sport === 'hurling' ? 'Hurling' : s.sport === 'camogie' ? 'Camogie' : s.sport === 'dual' ? 'Dual (Football & Hurling)' : s.sport === 'dual_ladies' ? 'Dual (Football & Camogie)' : s.sport}
                        {' · '}{s.gender === 'girls' ? 'Ladies' : 'Boys'}
                        {s.current_grade ? ' · ' + s.current_grade : ''}
                  </p>
                    </div>
                    <button onClick={() => { setEditingSquad(s); setSquadEditForm({ name: s.name, current_grade: s.current_grade || '', sport: s.sport || 'football', gender: s.gender || 'boys' }) }}
                      style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
                      ✏️ Edit
                    </button>
                  </div>
                  {showNewSeason?.id === s.id ? (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '0.5px solid #f0f0f0' }}>
                      <p style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>New season year</p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input type="number" style={{ ...adminInput, marginBottom: 0, flex: 1 }}
                          value={newSeasonYear} onChange={e => setNewSeasonYear(e.target.value)} />
                        <button onClick={() => startNewSeason(s)}
                          style={{ padding: '10px 14px', background: '#3B6D11', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                          Start
                        </button>
                        <button onClick={() => setShowNewSeason(null)}
                          style={{ padding: '10px 14px', border: '0.5px solid #ccc', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '13px' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setShowNewSeason(s)}
                      style={{ marginTop: '8px', fontSize: '12px', padding: '5px 10px', borderRadius: '6px', border: '0.5px solid #3B6D11', background: '#EAF3DE', color: '#3B6D11', cursor: 'pointer' }}>
                      🗓 Start New Season
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'pending' && (
        <>
          {pendingCoaches.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
              <p style={{ fontSize: '32px', marginBottom: '12px' }}>✅</p>
              <p>No pending requests</p>
              <p style={{ fontSize: '12px', marginTop: '6px', color: '#aaa' }}>Coaches who sign up for {clubName} will appear here</p>
            </div>
          ) : (
            pendingCoaches.map(coach => (
              <PendingCoachCard key={coach.id} coach={coach} squads={squads}
                onApprove={approveCoach} onReject={rejectCoach} loading={loading} isClubAdmin={isClubAdmin} />
            ))
          )}
        </>
      )}

      {activeTab === 'coaches' && (
        <>
          {coaches.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
              <p style={{ fontSize: '32px', marginBottom: '12px' }}>👥</p>
              <p>No coaches yet</p>
              <p style={{ fontSize: '12px', marginTop: '6px', color: '#aaa' }}>Invite coaches using the button above</p>
            </div>
          ) : (
            coaches.map(coach => (
              <CoachCard key={coach.id} coach={coach} squads={squads}
                onAssign={assignToSquad} onRemoveFromSquad={removeFromSquad}
                onUpdateRole={updateRole} onRemove={removeCoach} roleLabel={roleLabel}
                isClubAdmin={isClubAdmin} />
            ))
          )}
        </>
      )}

      {activeTab === 'review' && <SeasonReview profile={profile} />}
    </div>
  )
}

function PendingCoachCard({ coach, squads, onApprove, onReject, loading, isClubAdmin }) {
  const [selectedSquad, setSelectedSquad] = useState('')
  const [selectedRole, setSelectedRole] = useState('coach')
  return (
    <div style={{ padding: '14px 16px', borderBottom: '0.5px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <div style={avatar}>{(coach.full_name || coach.email || '?')[0].toUpperCase()}</div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '14px', fontWeight: '500' }}>{coach.full_name || 'Unknown'}</p>
          <p style={{ fontSize: '12px', color: '#888' }}>{coach.email}</p>
          <p style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>Requested to join</p>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
        <select style={selectStyle} value={selectedSquad} onChange={e => setSelectedSquad(e.target.value)}>
          <option value="">Assign to squad *</option>
          {squads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select style={selectStyle} value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
          <option value="coach">Coach</option>
          <option value="squad_admin">Squad Admin</option>
          {isClubAdmin && <option value="club_admin">Club Admin</option>}
        </select>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={() => onApprove(coach.id, selectedSquad, selectedRole)}
          disabled={loading || !selectedSquad}
          style={{ flex: 1, padding: '10px', background: selectedSquad ? '#3B6D11' : '#ccc', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: selectedSquad ? 'pointer' : 'default' }}>
          ✅ Approve
        </button>
        <button onClick={() => onReject(coach.id)}
          style={{ flex: 1, padding: '10px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #A32D2D', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
          ❌ Reject
        </button>
      </div>
    </div>
  )
}

function CoachCard({ coach, squads, onAssign, onRemoveFromSquad, onUpdateRole, onRemove, roleLabel, isClubAdmin }) {
  const [showAssign, setShowAssign] = useState(false)
  const [selectedSquad, setSelectedSquad] = useState('')
  const [selectedRole, setSelectedRole] = useState('coach')
  const role = roleLabel(coach.role)
  const assignedSquads = coach.squad_members || []
  return (
    <div style={{ padding: '14px 16px', borderBottom: '0.5px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <div style={avatar}>{(coach.full_name || '?')[0].toUpperCase()}</div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '14px', fontWeight: '500' }}>{coach.full_name || 'Unknown'}</p>
          <p style={{ fontSize: '12px', color: '#888' }}>{coach.email}</p>
        </div>
        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: role.bg, color: role.color, fontWeight: '500' }}>
          {role.label}
        </span>
      </div>
    {assignedSquads.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
          {assignedSquads.map(sm => (
            <span key={sm.squad_id} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#f0f0f0', color: '#555', display: 'flex', alignItems: 'center', gap: '4px' }}>
              {sm.squads?.name} · {sm.role === 'squad_admin' ? 'Admin' : 'Coach'}
              <button onClick={() => onRemoveFromSquad(coach.id, sm.squad_id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '12px', padding: '0', lineHeight: 1 }}>✕</button>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#FAEEDA', color: '#854F0B' }}>
            ⚠️ No squad assigned
          </span>
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <button onClick={() => setShowAssign(!showAssign)}
          style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
          + Squad
        </button>
        {/* Promote to Squad Admin — available to club admins for coaches */}
        {isClubAdmin && coach.role === 'coach' && (
          <button onClick={() => onUpdateRole(coach.id, 'squad_admin')}
            style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #854F0B', background: '#FAEEDA', color: '#854F0B', cursor: 'pointer' }}>
            → Squad Admin
          </button>
        )}
        {/* Promote to Club Admin — available to club admins for non-club-admins */}
        {isClubAdmin && coach.role !== 'club_admin' && (
          <button onClick={() => onUpdateRole(coach.id, 'club_admin')}
            style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
            → Club Admin
          </button>
        )}
        {/* Demote to Coach — available to club admins for squad/club admins */}
        {isClubAdmin && (coach.role === 'squad_admin' || coach.role === 'club_admin') && (
          <button onClick={() => onUpdateRole(coach.id, 'coach')}
            style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #888', background: '#f8f8f8', color: '#888', cursor: 'pointer' }}>
            → Coach
          </button>
        )}
        <button onClick={() => onRemove(coach.id)}
          style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #A32D2D', background: '#FCEBEB', color: '#A32D2D', cursor: 'pointer' }}>
          Remove
        </button>
      </div>
      {showAssign && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '6px', marginTop: '10px' }}>
          <select style={selectStyle} value={selectedSquad} onChange={e => setSelectedSquad(e.target.value)}>
            <option value="">Select squad</option>
            {squads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select style={selectStyle} value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
            <option value="coach">Coach</option>
            <option value="squad_admin">Squad Admin</option>
          </select>
          <button onClick={() => { onAssign(coach.id, selectedSquad, selectedRole); setShowAssign(false) }}
            style={{ padding: '6px 12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
            Save
          </button>
        </div>
      )}
    </div>
  )
}

const header = { padding: '16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', alignItems: 'center' }
const h2 = { fontSize: '17px', fontWeight: '500', flex: 1 }
const addBtn = { padding: '6px 14px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '20px', fontSize: '13px', cursor: 'pointer' }
const formBox = { margin: '12px 16px', padding: '14px', background: '#f8f8f8', borderRadius: '12px' }
const formTitle = { fontSize: '13px', fontWeight: '500', marginBottom: '8px' }
const inputStyle = { display: 'block', width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', background: 'white', boxSizing: 'border-box' }
const primaryBtn = { display: 'block', width: '100%', padding: '12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }
const avatar = { width: '36px', height: '36px', borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '600', color: '#185FA5', flexShrink: 0 }
const selectStyle = { padding: '8px 10px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', background: 'white', width: '100%' }

function ClubOfficers({ profile, isClubAdmin, isSquadAdmin }) {
  const [chairman, setChairman] = useState({ name_en: '', name_ga: '', email: '', foireann_id: '' })
  const [secretary, setSecretary] = useState({ name_en: '', name_ga: '', email: '', foireann_id: '' })
  const [editing, setEditing] = useState(false)
  const [editChairman, setEditChairman] = useState({ name_en: '', name_ga: '', email: '', foireann_id: '' })
  const [editSecretary, setEditSecretary] = useState({ name_en: '', name_ga: '', email: '', foireann_id: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const canEdit = isClubAdmin || isSquadAdmin

  useEffect(() => { if (profile?.club_id) load() }, [profile?.club_id])

  async function load() {
    const { data } = await supabase.from('clubs').select('chairman, secretary').eq('id', profile.club_id).maybeSingle()
    if (data) {
      setChairman(data.chairman || { name_en: '', name_ga: '', email: '', foireann_id: '' })
      setSecretary(data.secretary || { name_en: '', name_ga: '', email: '', foireann_id: '' })
    }
  }

  function startEditing() {
    setEditChairman(chairman)
    setEditSecretary(secretary)
    setEditing(true)
    setError('')
  }

  async function save() {
    if (!editChairman.name_en?.trim() && !editChairman.name_ga?.trim()) {
      setError('Chairman needs at least an English or Irish name.')
      return
    }
    if (!editSecretary.name_en?.trim() && !editSecretary.name_ga?.trim()) {
      setError('Secretary needs at least an English or Irish name.')
      return
    }
    setSaving(true); setError('')
    const { error: saveError } = await supabase.from('clubs').update({
      chairman: editChairman, secretary: editSecretary
    }).eq('id', profile.club_id)
    if (saveError) { setError('Error saving: ' + saveError.message); setSaving(false); return }
    setChairman(editChairman)
    setSecretary(editSecretary)
    setEditing(false)
    setSaving(false)
  }

  function displayName(person) {
    if (person.name_en?.trim()) return person.name_en
    if (person.name_ga?.trim()) return person.name_ga
    return null
  }

  if (!canEdit && !displayName(chairman) && !displayName(secretary)) return null

  return (
    <div style={{ margin: '12px 16px', padding: '14px', background: '#f8f8f8', borderRadius: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <p style={{ fontSize: '13px', fontWeight: '500', color: '#333' }}>Club Officers</p>
        {canEdit && !editing && (
          <button onClick={startEditing}
            style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
            ✏️ Edit
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: '#FCEBEB', border: '0.5px solid #A32D2D', borderRadius: '8px', padding: '8px 10px', marginBottom: '10px', fontSize: '12px', color: '#A32D2D' }}>
          {error}
        </div>
      )}

      {!editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <p style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Chairman</p>
            <p style={{ fontSize: '13px', color: '#333' }}>{displayName(chairman) || '—'}</p>
          </div>
          <div>
            <p style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Secretary</p>
            <p style={{ fontSize: '13px', color: '#333' }}>{displayName(secretary) || '—'}</p>
          </div>
        </div>
      ) : (
        <>
          <p style={{ fontSize: '12px', fontWeight: '500', color: '#185FA5', marginTop: '4px', marginBottom: '6px' }}>Chairman</p>
          <input style={officerInput} placeholder="Name (English)" value={editChairman.name_en || ''}
            onChange={e => setEditChairman({ ...editChairman, name_en: e.target.value })} />
          <input style={officerInput} placeholder="Name (Irish, optional)" value={editChairman.name_ga || ''}
            onChange={e => setEditChairman({ ...editChairman, name_ga: e.target.value })} />
          <input style={officerInput} placeholder="Email (optional)" type="email" value={editChairman.email || ''}
            onChange={e => setEditChairman({ ...editChairman, email: e.target.value })} />
          <input style={officerInput} placeholder="Foireann ID (optional)" value={editChairman.foireann_id || ''}
            onChange={e => setEditChairman({ ...editChairman, foireann_id: e.target.value })} />

          <p style={{ fontSize: '12px', fontWeight: '500', color: '#185FA5', marginTop: '12px', marginBottom: '6px' }}>Secretary</p>
          <input style={officerInput} placeholder="Name (English)" value={editSecretary.name_en || ''}
            onChange={e => setEditSecretary({ ...editSecretary, name_en: e.target.value })} />
          <input style={officerInput} placeholder="Name (Irish, optional)" value={editSecretary.name_ga || ''}
            onChange={e => setEditSecretary({ ...editSecretary, name_ga: e.target.value })} />
          <input style={officerInput} placeholder="Email (optional)" type="email" value={editSecretary.email || ''}
            onChange={e => setEditSecretary({ ...editSecretary, email: e.target.value })} />
          <input style={officerInput} placeholder="Foireann ID (optional)" value={editSecretary.foireann_id || ''}
            onChange={e => setEditSecretary({ ...editSecretary, foireann_id: e.target.value })} />

          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button onClick={save} disabled={saving}
              style={{ flex: 1, padding: '10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setError('') }}
              style={{ flex: 1, padding: '10px', border: '0.5px solid #ccc', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '13px' }}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const officerInput = { display: 'block', width: '100%', padding: '8px 10px', marginBottom: '8px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', background: 'white', boxSizing: 'border-box' }
const adminInput = { display: 'block', width: '100%', padding: '8px 10px', marginBottom: '8px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', background: 'white', boxSizing: 'border-box' }